import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import type { MeResponse } from "../../server/routes/companies";
import type { DatasetId } from "../../server/servicem8/datasets";
import type { SyncResponse } from "../../shared/sync";
import { api, ApiError } from "./api";

export type Me = MeResponse;
export type CompanySummary = MeResponse["companies"][number];

export function useMe() {
    return useQuery({
        queryKey: ["me"],
        queryFn: () => api<Me>("/me").catch((e) => (e instanceof ApiError && e.status === 401 ? null : Promise.reject(e))),
        staleTime: 30_000,
    });
}

/**
 * Query data that's derived from synced ServiceM8 data. List every dataset the data depends on in
 * `objects` (ids from server/servicem8/datasets.ts); the realtime layer refetches it whenever any
 * of them change, whether the change came from ServiceM8 or another user here.
 * `alsoOn` lists other tools' app-owned data it shows (the first part of their query key), so it
 * refetches when an `invalidate` event is published for those too.
 * Add `fresh: true` to also ask for those datasets to be brought up to date (see useFresh).
 */
export function useSm8Query<T>(opts: {
    companyId: string;
    objects: DatasetId[];
    key: unknown[];
    path: string;
    alsoOn?: string[];
    fresh?: boolean;
    /** False: not fetched at all (e.g. this person isn't allowed to see it). */
    enabled?: boolean;
    /** Updates itself record by record: told which records changed (or "all") instead of being refetched (lib/realtime.ts). */
    onChange?: (change: Record<string, string[]> | "all") => void;
}) {
    const enabled = opts.enabled ?? true;
    useFresh(opts.companyId, opts.fresh && enabled ? opts.objects : []);
    return useQuery({
        enabled,
        queryKey: ["c", opts.companyId, ...opts.key],
        queryFn: () => api<T>(`/c/${opts.companyId}${opts.path}`),
        meta: { sm8: opts.objects, alsoOn: opts.alsoOn, onChange: opts.onChange },
    });
}

/**
 * This page shows these datasets: ask the server to bring any that are older than their
 * "fresh for" time up to date (e.g. the Price List's materials). Asked when the page opens and
 * whenever the tab comes back into view; the new data arrives through the usual live updates.
 */
export function useFresh(companyId: string, datasets: DatasetId[]) {
    const key = datasets.join(",");
    useEffect(() => {
        if (!key) return;
        const ask = () =>
            void api(`/c/${companyId}/sync/fresh`, { body: { datasets: key.split(",") } }).catch(() => {
                // Best effort: the routine sync still runs.
            });
        ask();
        const onVisible = () => document.visibilityState === "visible" && ask();
        document.addEventListener("visibilitychange", onVisible);
        return () => document.removeEventListener("visibilitychange", onVisible);
    }, [companyId, key]);
}

/**
 * This page shows one record (e.g. a job): fetch it from ServiceM8 as it is now (at most every 30
 * seconds per record). The fresh copy arrives through the usual live updates.
 */
export function useFreshRecord(companyId: string, object: DatasetId, uuid: string | null | undefined) {
    useEffect(() => {
        if (!uuid) return;
        void api(`/c/${companyId}/sync/record`, { body: { object, uuid } }).catch(() => {
            // Best effort: the synced copy is shown meanwhile.
        });
    }, [companyId, object, uuid]);
}

/** The sync's state, for Settings › ServiceM8 and the header's sync status. Refetched on sync.changed. */
export function useSyncStatus(companyId: string, enabled = true) {
    return useQuery({
        queryKey: ["c", companyId, "sync"],
        queryFn: () => api<SyncResponse>(`/c/${companyId}/sync`),
        enabled,
        // Keeps "2 minutes ago" and next-run times current between events.
        refetchInterval: 60_000,
    });
}
